import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import Checkbox from '../../elements/Checkbox';
import { PolicyType } from '../../types/enums';
import { exclusionsAndIneligibleActivitiesDisclaimerText } from './CheckoutForm';

export interface ScrollableAcknowledgmentsProps {
  stateNoticeText: string;
  applicationTypes: PolicyType[];
  acceptedTermsAndConditions: boolean;
  setAcceptedTermsAndConditions: React.Dispatch<React.SetStateAction<boolean>>;
  readAcknowledgements: boolean;
  setReadAcknowledgements: React.Dispatch<React.SetStateAction<boolean>>;
  getExclusionSummaryUrl: () => string;
  hasExclusions: boolean;
  hasProhibitedOperations: boolean;
}

const ScrollableAcknowledgments = ({
  stateNoticeText,
  applicationTypes,
  acceptedTermsAndConditions,
  setAcceptedTermsAndConditions,
  readAcknowledgements,
  setReadAcknowledgements,
  getExclusionSummaryUrl,
  hasExclusions,
  hasProhibitedOperations,
}: ScrollableAcknowledgmentsProps) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollableBoxRef = useRef<HTMLDivElement>(null);
  const isPlOnly = applicationTypes.length === 1 && applicationTypes.includes(PolicyType.PL);

  const handleScroll = () => {
    if (scrollableBoxRef.current && !hasScrolledToBottom) {
      const { scrollTop, scrollHeight, clientHeight, offsetHeight } = scrollableBoxRef.current;
      const hasScrolled = scrollTop + clientHeight >= scrollHeight - (offsetHeight - clientHeight);
      setHasScrolledToBottom(hasScrolled);
    }
  };

  return (
    <>
      <ScrollableAcknowledgmentBox
        ref={scrollableBoxRef}
        onScroll={handleScroll}
        data-testid="scrollable-acknowledgment-box"
      >
        <AcknowledgmentLabel>Acknowledgments</AcknowledgmentLabel>
        <StateNoticeText>{stateNoticeText}</StateNoticeText>
        <FeesMessage>
          All policy premiums, taxes, surcharges and applicable fees have been reviewed prior to purchase in accordance
          with state requirements.
        </FeesMessage>
        {(hasExclusions || hasProhibitedOperations) && !isPlOnly && (
          <FeesMessage>
            <strong>Exclusions and Ineligible Activities: </strong>
            {exclusionsAndIneligibleActivitiesDisclaimerText}
          </FeesMessage>
        )}
        <FeesMessage>
          <strong>Terms & Conditions:</strong> Section 21 contains provisions that limit our liability to you, require
          you to waive your right to a trial by jury or a class action suit. As you are accepting these terms on behalf
          of your client, you present and warrant that you have disclosed these terms to your client and that you have
          received express authorization from your client to accept these terms on their behalf and that such
          authorization expressly includes a commitment from your client to utilize the risk management program,
          services or platform as applicable.
        </FeesMessage>
        <FeesMessage>
          <strong>Submission of Data:</strong> You acknowledge that you were granted permission to provide the required
          data elements to Coterie by the policyholder. Coterie's{' '}
          <Link href={`https://${import.meta.env.VITE_APP_DOMAIN}/privacy-policy`} target="_blank" rel="noopener">
            Privacy Policy
          </Link>
          , linked below, explains how we collect, use, disclose, and protect personal information. By reviewing this
          policy and utilizing our services, you acknowledge and consent to our handling of your and your policyholder's
          data as described. You are required to review the full policy to understand our data practices and your
          rights. You may withdraw your consent at any time by contacting{' '}
          <Link href="mailto:privacy@coterieinsurance.com">privacy@coterieinsurance.com</Link>. Please note that
          withdrawing consent may limit or prevent access to certain services that rely on your data, as permitted by
          applicable law.
        </FeesMessage>
      </ScrollableAcknowledgmentBox>
      <ScrollToBottomWarning $visible={!hasScrolledToBottom}>
        <WarningIcon>!</WarningIcon>
        <WarningText>SCROLL TO THE BOTTOM TO ACKNOWLEDGE</WarningText>
      </ScrollToBottomWarning>
      <AcknowledgmentTermsCont>
        <AcknowledgmentTermsRow>
          <AcknowledgmentTermsCheckbox
            onChange={() => {
              if (hasScrolledToBottom) {
                setAcceptedTermsAndConditions((prev) => !prev);
              }
            }}
            selected={acceptedTermsAndConditions}
            id="termsConditionsNoticeCheckbox"
            disabled={!hasScrolledToBottom}
          />
          <AcknowledgmentTermsLabel htmlFor="termsConditionsNoticeCheckbox">
            <LabelText $disabled={!hasScrolledToBottom}>
              I acknowledge that I have read and agreed to the above conditions. I have reviewed Coterie's{' '}
            </LabelText>
            <Link href={`https://${import.meta.env.VITE_APP_DOMAIN}/terms-conditions`} target="_blank" rel="noopener">
              Terms & Conditions
            </Link>
            <LabelText $disabled={!hasScrolledToBottom}> and </LabelText>
            <Link href={`https://${import.meta.env.VITE_APP_DOMAIN}/privacy-policy`} target="_blank" rel="noopener">
              Privacy Policy
            </Link>
            <LabelText $disabled={!hasScrolledToBottom}>.</LabelText>
          </AcknowledgmentTermsLabel>
        </AcknowledgmentTermsRow>
      </AcknowledgmentTermsCont>
      {applicationTypes.includes(PolicyType.PL) && (
        <AcknowledgmentTermsCont>
          <AcknowledgmentTermsRow>
            <AcknowledgmentTermsCheckbox
              onChange={() => {
                if (hasScrolledToBottom) {
                  setReadAcknowledgements((prev) => !prev);
                }
              }}
              selected={readAcknowledgements}
              id="plAcknowledgementsCheckbox"
              disabled={!hasScrolledToBottom}
            />
            <AcknowledgmentTermsLabel htmlFor="plAcknowledgementsCheckbox">
              <LabelText>Yes, I have reviewed and understand the Professional Liability </LabelText>
              <Link href={getExclusionSummaryUrl()} target="_blank" rel="noopener" aria-label="pl exclusions link">
                Terms and Exclusions.
              </Link>
            </AcknowledgmentTermsLabel>
          </AcknowledgmentTermsRow>
        </AcknowledgmentTermsCont>
      )}
    </>
  );
};

const ScrollableAcknowledgmentBox = styled.div`
  display: flex;
  flex-direction: column;
  height: 275px;
  overflow-y: scroll;
  border-radius: 8px;
  border: 1px solid #d4d4dd;
  padding: 16px;
  margin-top: 8px;
  margin-bottom: 16px;

  @media print {
    display: none;
  }
`;

const ScrollToBottomWarning = styled.div<{ $visible: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  background-color: ${(props) => props.theme.colors.alert.ten};
  border-radius: 4px;
  padding: 8px 12px;
  transition: all 0.3s ease-in-out;
  height: ${(props) => (props.$visible ? 'auto' : '0')};
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  overflow: hidden;
  padding: ${(props) => (props.$visible ? '8px 12px' : '0 12px')};
  margin-bottom: ${(props) => (props.$visible ? '24px' : '0')};

  @media print {
    display: none;
  }
`;

const WarningIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.alert.dark};
  color: white;
  font-size: 12px;
  font-weight: bold;
`;

const WarningText = styled.span`
  color: ${(props) => props.theme.colors.alert.dark};
  font-family: ${(props) => props.theme.font.typeface.secondary};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const AcknowledgmentTermsRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 8px;
  column-gap: 8px;
  row-gap: 8px;
`;

const AcknowledgmentTermsCont = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0;
  padding: 0 0 16px 0;

  @media print {
    display: none;
  }
`;

const AcknowledgmentTermsCheckbox = styled(Checkbox)`
  margin-left: 8px;
`;

const AcknowledgmentLabel = styled.p`
  color: ${(props) => props.theme.colors.tertiary.zero};
  font-family: ${(props) => props.theme.font.typeface.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 15px;
  margin: 0;
`;

const StateNoticeText = styled.p`
  margin-top: 8px;
  color: ${(props) => props.theme.colors.neutral.medium};
  font-family: ${(props) => props.theme.font.typeface.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  white-space: pre-line;
`;

const FeesMessage = styled.p`
  color: ${(props) => props.theme.colors.neutral.medium};
  font-family: ${(props) => props.theme.font.typeface.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  margin: 6px 0;
`;

const AcknowledgmentTermsLabel = styled.label`
  color: ${(props) => props.theme.pageComponents.summary.checkoutForm.fraudLabel.textColor};
  font-family: ${(props) => props.theme.font.typeface.secondary};
  font-weight: 400;
  font-size: 16px;
`;

const Link = styled.a`
  color: ${(props) => props.theme.pageComponents.summary.checkoutForm.link.textColor};
  font-weight: bold;
  text-decoration: none;
`;

const LabelText = styled.span<{ $disabled?: boolean }>`
  line-height: 23px;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};

  :hover {
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  }
`;

export default ScrollableAcknowledgments;
